// 年度平旺假日規劃的本機狀態。
//
// 這一頁的定位是排表當下的工作區，不是年度定義表的權威來源，所以後端不落地，
// 人工調整與門檻設定都存在瀏覽器。代價是換裝置或清除瀏覽器資料就會消失 ——
// 頁面上要明講這件事。

const OVERRIDE_KEY = (year) => `rate-calendar:${year}`;
const SETTINGS_KEY = 'rate-calendar:settings';

// 沿用後端 price_types 的 id，兩邊講同一種話
export const LEVELS = [
  'normal',
  'peak-season-normal',
  'holiday',
  'peak-season-holiday',
  'special-holiday',
];

export const LEVEL_LABELS = {
  normal: '平日',
  'peak-season-normal': '旺日',
  holiday: '假日',
  'peak-season-holiday': '旺假日',
  'special-holiday': '大假日',
};

// 與後端 CalendarExporter::FILLS 一致，網頁與匯出的 Excel 對照得起來
export const LEVEL_COLORS = {
  normal: '#ffffff',
  'peak-season-normal': '#4caf50',
  holiday: '#f8a5d0',
  'peak-season-holiday': '#e07b18',
  'special-holiday': '#e53935',
};

// 日曆格子的「有建議」與「人工指定」樣式，YearCalendar 的格子與圖例共用同一份 ——
// 兩邊各自寫一份色碼，改一邊很容易忘了改另一邊，圖例就會跟格子對不上。
export const SUGGESTED_BORDER = '2px solid #1677ff';
export const MANUAL_DOT_STYLE = {
  position: 'absolute',
  top: 1,
  right: 2,
  width: 4,
  height: 4,
  borderRadius: '50%',
  background: '#000',
};

export const DEFAULT_SETTINGS = {
  peakHolidayRun: 3,
  specialHolidayRun: 5,
  peakThreshold: 15,
  targetOccupancy: 70,
  tolerance: 10,
  // 旅行社包團走合約價，不受公告的平旺假日表影響。預設不排除 —— 那會改變
  // 住房率的口徑，是個要使用者自己決定的判斷，不該悄悄替他決定。
  excludeGroups: false,
};

function read(key) {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    // 陣列與 null 都不是我們要的形狀，退回空物件比讓頁面掛掉好
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
}

export function loadOverrides(year) {
  return read(OVERRIDE_KEY(year));
}

/** 稀疏寫入：只記被改過的日期，沒改的跟著系統重算。 */
export function saveOverride(year, date, level) {
  const overrides = { ...loadOverrides(year), [date]: level };
  window.localStorage.setItem(OVERRIDE_KEY(year), JSON.stringify(overrides));
  return overrides;
}

/**
 * 一次寫入多天，用於「套用全部建議」。
 *
 * 不用 saveOverride 逐日呼叫：那會逐日重讀 localStorage 再整份寫回，
 * 八十幾筆建議就是八十幾次讀寫，而且中途出錯會留下寫到一半的狀態。
 * 這裡合併成一次寫入，要嘛整批成立、要嘛完全沒動。
 *
 * @param {Object<string,string>} levelByDate ['Y-m-d' => 級別]
 */
export function saveOverrides(year, levelByDate) {
  const overrides = { ...loadOverrides(year), ...levelByDate };
  window.localStorage.setItem(OVERRIDE_KEY(year), JSON.stringify(overrides));
  return overrides;
}

/** 只清該年度的逐日調整。門檻設定的生命週期不同，不在這裡動。 */
export function clearOverrides(year) {
  window.localStorage.removeItem(OVERRIDE_KEY(year));
  return {};
}

/**
 * 只重設單一日期，改錯一天不必連累整年已經調好的其他日期。
 *
 * 移除該鍵而不是寫入 null —— 稀疏儲存的語意是「這一頁沒意見的日期，鍵根本不存在」，
 * 寫 null 會讓這天變成「明確指定為 null」，之後 `overrides[date]` 判斷式仍然要
 * 額外處理 null 才會落回規則基準，多一種要記得的狀態。
 */
export function clearOverride(year, date) {
  const overrides = { ...loadOverrides(year) };
  delete overrides[date];
  window.localStorage.setItem(OVERRIDE_KEY(year), JSON.stringify(overrides));
  return overrides;
}

/** 缺欄位一律以預設值補齊，避免 undefined 流進 API 查詢參數。 */
export function loadSettings() {
  return { ...DEFAULT_SETTINGS, ...read(SETTINGS_KEY) };
}

export function saveSettings(settings) {
  const merged = { ...DEFAULT_SETTINGS, ...settings };
  window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(merged));
  return merged;
}

export function resetSettings() {
  window.localStorage.removeItem(SETTINGS_KEY);
  return { ...DEFAULT_SETTINGS };
}

/**
 * 決定每一天最終顯示的級別與它的來源。
 *
 * 三種來源：規則基準 (base)、調整建議 (suggested)、人工指定 (manual)。
 *
 * 這一頁的權威是規則，不是資料 —— level **永遠**是 overrides[date] ?? base_level，
 * 絕不取 suggested_level。讓建議預設生效等於讓資料在沒有人確認的情況下直接
 * 進到對外公告表，那不是這一頁該做的事。'suggested' 標的是「規則對這天算出
 * 的級別，資料對它有意見，值得看一眼」，不是「這一格已經照建議改了」。要接受
 * 建議，得在建議清單按「採納」——那會把建議的級別寫進 overrides，使這天變成
 * manual，之後就跟規則的重算脫鉤。
 *
 * 人工指定一律覆蓋規則與建議，且重新查詢時不被覆寫 —— 否則調到一半拉一下
 * 滑桿就全沒了。沒被指定的日期則跟著新的查詢結果更新，這正是稀疏儲存的好處。
 */
export function resolveDays(days, overrides) {
  return days.map((day) => {
    if (overrides[day.date]) {
      return { ...day, level: overrides[day.date], state: 'manual' };
    }
    const state = day.suggested_level !== day.base_level ? 'suggested' : 'base';
    return { ...day, level: day.base_level, state };
  });
}
